import type {
  MeetingGradeSettings,
  MeetingGradeSettingsDto,
  MeetingMentorFeedback,
  MeetingMentorFeedbackDto,
  MeetingMyStudentsOverview,
  MeetingMyStudentsOverviewDto,
  MeetingMyStudentsOverviewMeetingDto,
  MeetingMyStudentsStudent,
  MeetingMyStudentsStudentDto,
  MeetingRatingGrade,
  MeetingRatingGradeDto,
  MeetingRatingOption,
  MeetingRatingOptionDto,
  MeetingStudentReport,
  MeetingStudentReportDto,
  MeetingStudentSelfRating,
  MeetingStudentSelfRatingDto,
  MeetingSubjectRating,
  MeetingSubjectRatingDto,
} from "meetings/student-meeting/types";
import { toAbsoluteImageUrl } from "shared/utils";

const ratingOptionFromDto = (
  dto: MeetingRatingOptionDto,
): MeetingRatingOption => ({
  position: dto.position,
  label: dto.label,
  icon: dto.icon,
});

export const gradeFromDto = (
  dto: MeetingRatingGradeDto,
): MeetingRatingGrade => ({
  type: dto.type,
  grade: dto.grade,
});

const studentSelfRatingFromDto = (
  dto: MeetingStudentSelfRatingDto,
): MeetingStudentSelfRating => ({
  rating: dto.rating,
  remark: dto.remark,
});

const subjectRatingFromDto = (
  dto: MeetingSubjectRatingDto,
): MeetingSubjectRating => ({
  id: dto.id,
  subject: dto.subject,
  teacherShortage: dto.teacher_shortage,
  lessonClass: dto.lesson_class,
  rating: dto.rating,
  feedback: dto.feedback,
  feedforward: dto.feedforward,
  grades: (dto.grades ?? []).map(gradeFromDto),
  allowStudentRatings: dto.allow_student_ratings,
  studentRating: dto.student_rating
    ? studentSelfRatingFromDto(dto.student_rating)
    : null,
  actionplanId: dto.actionplan_id ?? null,
});

const studentReportFromDto = (
  dto: MeetingStudentReportDto,
): MeetingStudentReport => ({
  invite: dto.invite,
  feedbackSeen: dto.feedback_seen,
  feedbackSent: dto.feedback_sent,
});

const mentorFeedbackFromDto = (
  dto: MeetingMentorFeedbackDto,
): MeetingMentorFeedback => ({
  shortage: dto.shortage,
  name: dto.name,
  profileImage: toAbsoluteImageUrl(dto.profile_image),
  summary: dto.summary,
  writtenAt: dto.written_at,
  canEdit: dto.can_edit,
});

export const studentFromDto = (
  dto: MeetingMyStudentsStudentDto,
): MeetingMyStudentsStudent => ({
  id: dto.id,
  firstName: dto.first_name,
  middleName: dto.middle_name,
  lastName: dto.last_name,
  formattedName: dto.formatted_name,
  fullName: dto.full_name,
  mentorClass: dto.mentor_class,
  profileImage: toAbsoluteImageUrl(dto.profile_image),
  mentors: dto.mentors.map((m) => ({
    id: m.id,
    name: m.name,
    shortage: m.shortage,
  })),
  ratings: dto.ratings.map(subjectRatingFromDto),
  ratingOptions: dto.rating_options.map(ratingOptionFromDto),
  meetingName: dto.meeting_name,
  progress: dto.progress,
  report: studentReportFromDto(dto.report),
  mentorFeedback: (dto.mentor_feedback ?? []).map(mentorFeedbackFromDto),
});

const gradeSettingsFromDto = (
  dto: MeetingGradeSettingsDto,
): MeetingGradeSettings => ({
  enableSeGrade: Boolean(dto.enable_se_grade),
  enableRapportGrade: Boolean(dto.enable_rapport_grade),
});

export const meetingFromDto = (
  dto: MeetingMyStudentsOverviewMeetingDto,
): MeetingMyStudentsOverview["meeting"] => ({
  id: dto.id,
  name: dto.name,
  status: dto.status,
  progressPercentage:
    dto.total_ratings > 0
      ? Math.round((dto.total_given_ratings / dto.total_ratings) * 100)
      : 0,
  closedModifyAt: dto.closed_modify_at ? new Date(dto.closed_modify_at) : null,
  ratingOptions: dto.rating_options.map(ratingOptionFromDto),
  questionPrompt: dto.question_prompt,
  gradeSettings: gradeSettingsFromDto(dto.grade_settings),
  mailStudent: dto.mail_student,
  shareFeedbackAt: dto.share_feedback_at,
});

export const myStudentsOverviewFromDto = (
  dto: MeetingMyStudentsOverviewDto,
): MeetingMyStudentsOverview => ({
  meeting: meetingFromDto(dto.meeting),
  students: dto.students.map(studentFromDto),
  meta: {
    hasMore: dto.meta.has_more,
    offset: dto.meta.offset,
    take: dto.meta.take,
    total: dto.meta.total,
  },
  enableStudentFilter: dto.enable_student_filter,
});
